/**
 * Module dependencies.
 */

import { RenderProps } from 'app/components/organisms/crud/form-widgets/render';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'app/core/utils/formatter';
import BigNumber from 'bignumber.js';
import { reduce } from 'lodash';
import { cn } from 'app/lib/utils';
import { FormInputField } from 'app/components/organisms/crud/form-components/form-input-field';
import { requiredRule } from 'app/core/utils/field-rules';

/**
 * Export `SplitTransactionStatus` component.
 */

export function SplitTransactionStatus({ form, item }: RenderProps): JSX.Element {
  const [translate] = useTranslation();
  const { name } = item;
  const splitTransactions = form.watch('splitTransactions');
  const amount = form.watch('amount');
  const consumed = reduce(
    splitTransactions,
    (acc: any, item: any) => {
      const amount = !isNaN(item?.amount) ? item?.amount : '0';

      return acc.plus(amount);
    },
    new BigNumber(0)
  ).toString();

  const handleRules = useCallback(() => {
    return {
      ...requiredRule(translate),
      validate: (value: string) => {
        return new BigNumber(value).isEqualTo('0') || 'error';
      }
    };
  }, [translate]);

  const isExpense = new BigNumber(amount).isLessThan('0');

  useEffect(() => {
    form.setValue(name, new BigNumber(consumed).minus(amount).toString());
  }, [amount, consumed, form, name]);

  const bnConsumed = new BigNumber(consumed);

  return (
    <>
      <div
        className={
          'sticky top-0 z-10 mb-4 flex gap-[60px] rounded-lg border border-[#ccc] bg-[#2f2f2f] p-4 shadow-[0_2px_8px_rgba(0,0,0,0.15)]'
        }
      >
        <div className={'flex'}>
          <div className={'text-sm font-medium text-white'}>{translate('common.labels.availableAmount')}</div>

          <div className={'ml-2 text-sm font-light text-[rgb(137,235,155)]'}>{formatCurrency(amount)}</div>
        </div>

        {consumed.toString() === amount.toString() ? (
          <div className={'flex items-center rounded-2xl bg-[rgb(29,147,51)] px-3 text-sm font-medium text-white'}>
            {translate('common.labels.valid')}
          </div>
        ) : (
          <>
            <div className={'flex'}>
              <div className={'text-sm font-medium text-white'}>{translate('common.labels.consumed')}</div>

              <div
                className={cn('ml-2 text-sm font-light text-[rgb(137,235,155)]', {
                  'text-[#f77777]': amount.toString() !== consumed.toString()
                })}
              >
                {formatCurrency(consumed)}
              </div>
            </div>

            {bnConsumed.isGreaterThan(amount) && (
              <div className={'flex'}>
                <div className={'text-sm font-medium text-white'}>
                  {isExpense ? translate('common.labels.add') : translate('common.labels.remove')}
                </div>

                <div className={'ml-2 text-sm font-light text-[#f77777]'}>
                  {formatCurrency(bnConsumed.minus(amount))}
                </div>
              </div>
            )}

            {bnConsumed.isLessThan(amount) && (
              <div className={'flex'}>
                <div className={'text-sm font-medium text-white'}>
                  {isExpense ? translate('common.labels.remove') : translate('common.labels.add')}
                </div>

                <div className={'ml-2 text-sm font-light text-[#f77777]'}>
                  {formatCurrency(bnConsumed.minus(amount))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className={'fixed -top-[99999px]'}>
        <FormInputField form={form} item={{ name, label: '', rules: handleRules(), type: 'inputField' }} name={name} />
      </div>
    </>
  );
}
